import { cloneDeep, isEmpty, get, find } from 'lodash';
import {
  getIntentCategories,
  getRecommendationKeys,
  getDefaultGetStartedApps,
  getStaffPickAppKeys,
  getStaffPickCategories,
  getStaffPicksMsg
} from './helpers';

export default ({
  psData = {},
  mtsData = {},
  isLoggedIn = false,
  experimentation,
  productIntentTools = [],
  merchantActivationState
}) => {
  let { tools = [], categories = [], sections = [] } = psData;
  const { recommended = [] } = psData;
  const { quizCategoryRow, staffPickExp } = experimentation;
  // filter the tools	if `mtsData` is not empty
  if (!isEmpty(mtsData)) {
    tools = tools.filter(({ appKey = '' }) => mtsData[appKey]);
  }
  const isQuizCategoryCardDisable = get(
    recommended,
    '0.isCategoryDisable',
    false
  );
  const isStaffPickEligible = staffPickExp;
  const isRecommendedCategoryEligible = recommended && recommended.length > 0;
  const isIntentEligible = productIntentTools && productIntentTools.length > 0;
  const isGetStartedEligible =
    !merchantActivationState && isLoggedIn && psData.getStartedMsg;
  const isQuizBannerLayout = !!quizCategoryRow;
  const noOfRecommendations =
    isQuizCategoryCardDisable === 'TRUE' || isQuizBannerLayout ? 4 : 2;
  const defaultRecommended = isRecommendedCategoryEligible
    ? tools.slice(0, noOfRecommendations)
    : [];
  // Filter out the first 4 tools if present
  // Order tools by total view Count 90Day
  let intentMessages = [];
  const intentHashMap = {};

  if (isIntentEligible && !isLoggedIn) {
    productIntentTools.forEach(({ name, totalCount90Day }) => {
      intentHashMap[name] = totalCount90Day;
    });

    intentMessages = tools
      .filter(tool => intentHashMap[tool.appKey])
      .sort((toolA, toolB) => {
        return (
          (intentHashMap[toolB.appKey] || 0) -
          (intentHashMap[toolA.appKey] || 0)
        );
      })
      .slice(0, 4);
  }

  if (isGetStartedEligible) {
    const { getStartedMsg = [] } = psData;

    if (isIntentEligible) {
      const appRankObj = getStartedMsg.reduce(
        (obj, item) => Object.assign(obj, { [item.appKey]: item.rank }),
        {}
      );

      const appKeyArr = productIntentTools.map(({ name }) => name);

      intentMessages = tools
        .filter(
          tool => appKeyArr.includes(tool.appKey) && tool.appKey in appRankObj
        )
        .sort((toolA, toolB) => {
          return (
            (appRankObj[toolA.appKey] || 0) - (appRankObj[toolB.appKey] || 0)
          );
        })
        .slice(0, 4);
    }

    const defaultGetStartedApps = getDefaultGetStartedApps(
      getStartedMsg,
      tools
    );

    defaultGetStartedApps.forEach(tool => {
      if (intentMessages.length < 4) {
        if (!find(intentMessages, { appKey: tool.appKey })) {
          intentMessages.push(tool);
        }
      }
    });
  }

  // ...then go back and filter out any sections that no longer have tools
  // sectionKey is defaulted to random string to filter out sections with
  // no content
  sections = sections.filter(({ sectionKey: sk = 'heisenberg' }) => {
    return tools.some(({ sectionKey = '' }) => sectionKey === sk);
  });

  // ...then go back and filter out any categories that no longer have tools
  // ditto as above for random default string for categoryKey
  categories = categories.filter(({ categoryKey: cK = 'schrodinger' }) => {
    return tools.some(({ categoryKey = '' }) => categoryKey === cK);
  });
  const args = {
    ...psData,
    tools,
    categories,
    sections,
    defaultRecommended,
    isLoggedIn,
    intentMessages,
    isStaffPickEligible
  };

  const structuredData = buildTree(args);

  tools = tools.reduce((acc, tool) => {
    acc[tool.appKey] = tool;
    return acc;
  }, {});

  sections = sections.reduce((acc, section) => {
    acc[section.sectionKey] = section;
    return acc;
  }, {});

  categories = categories.reduce((acc, category) => {
    acc[category.categoryKey] = category;
    return acc;
  }, {});

  const flatData = {
    ...psData,
    tools,
    categories,
    sections,
    defaultRecommended,
    isRecommendedCategoryEligible,
    isQuizBannerLayout,
    intentMessages,
    isMerchantActivated: merchantActivationState
  };

  return {
    flatData,
    structuredData
  };
};

const buildTree = psData => {
  const deepCopy = cloneDeep(psData);
  const {
    tools = [],
    sections = [],
    main = [],
    staffPickMsg = [],
    defaultRecommended = [],
    isLoggedIn,
    intentMessages = [],
    isStaffPickEligible
  } = deepCopy;
  let { categories = [] } = deepCopy;
  const staffPickDisplayed = getStaffPicksMsg(
    staffPickMsg,
    defaultRecommended,
    isLoggedIn,
    tools
  );
  const staffPickAppKeys = getStaffPickAppKeys(staffPickDisplayed);
  const staffPickCategory = getStaffPickCategories(staffPickAppKeys, tools);
  const root = {};
  const [mainContent = {}] = main;
  const recommendations = getRecommendationKeys(defaultRecommended);
  root.content = mainContent;
  categories = categories.map(c =>
    handleCategory(
      c,
      sections,
      tools,
      recommendations,
      isLoggedIn,
      staffPickCategory,
      staffPickAppKeys,
      intentMessages,
      isStaffPickEligible
    )
  );
  root.categories = categories;
  root.staffPickDisplayed = staffPickDisplayed;
  root.intentToolsDisplayed = intentMessages;
  return root;
};

const handleCategory = (
  category,
  sections,
  tools,
  recommendations,
  isLoggedIn,
  staffPickCategory,
  staffPickAppKeys,
  intentMessages,
  isStaffPickEligible
) => {
  const newCategory = { content: category };
  const { categoryKey = '' } = category;
  const relevantSections = getSectionsForCategory(categoryKey, sections, tools);
  const toolsForCategory = getTools(categoryKey, tools);
  const intentRowKeys = intentMessages.map(item => item.appKey);
  const intentRowCategory = getIntentCategories(intentRowKeys, tools);
  const featuredTools = getFeaturedTools({
    categoryKey,
    toolsForCategory,
    recommendations,
    staffPickCategory,
    staffPickAppKeys,
    isLoggedIn,
    intentRowKeys,
    intentRowCategory,
    isStaffPickEligible
  });

  newCategory.featured = featuredTools;
  if (relevantSections.length) {
    newCategory.sections = relevantSections;
  } else {
    newCategory.tools = toolsForCategory;
  }
  return newCategory;
};

const getSectionsForCategory = (key, sections, tools) => {
  sections = sections.filter(({ categoryKey = '' }) => categoryKey === key);
  sections = sections.map(s => {
    return {
      content: s,
      tools: getTools(s.sectionKey, tools)
    };
  });
  return sections;
};

const getTools = (key, tools) => {
  return tools.filter(t => {
    return t.categoryKey === key || t.sectionKey === key;
  });
};

const getFeaturedTools = ({
  categoryKey,
  toolsForCategory,
  recommendations,
  staffPickCategory,
  staffPickAppKeys,
  isLoggedIn,
  intentRowKeys,
  intentRowCategory,
  isStaffPickEligible
}) => {
  const { recommendedCategories, recommendedApps } = recommendations;

  // if the category of either staff pick, intent row or recommendedApp is not in categoryKey,
  //then the app should return the first 2 apps of the category.
  //this way there is not need to travel to the rest of the logic
  const shouldReturnFirst2Apps =
    toolsForCategory.length <= 2 ||
    (recommendedApps.length === 0 &&
      staffPickAppKeys.length === 0 &&
      intentRowKeys.length === 0) ||
    (!recommendedCategories.includes(categoryKey) &&
      !staffPickCategory.includes(categoryKey) &&
      !intentRowCategory.includes(categoryKey));

  if (shouldReturnFirst2Apps) return toolsForCategory.slice(0, 2);

  /*
    If an app within a pillar category is already in the `Recommended For You`
    section, choose the other apps to feature in that pillar - e.q. if `PayPal
    Checkout` is in `Recommended For You` section, `Accept Payments` pillar should
    feature the 2nd and 3rd ranking apps, such as `Invoicing` and `Subscriptions`.

    If there are no other apps in that pillar to feature, use the recommended app in the pillar
    anyway - e.q. if `Fraud Protection` is in `Recommended For You`
    section, it should still feature it in the `Risk` pillar as it is the only app).
  */
  const featuredTools = [];

  for (let i = 0; i < toolsForCategory.length; i++) {
    if (featuredTools.length === 2) break;

    const isRecommended = recommendedApps.includes(toolsForCategory[i].appKey);
    const isInStaffPick =
      isStaffPickEligible &&
      staffPickAppKeys.includes(toolsForCategory[i].appKey);
    const isIntent = intentRowKeys.includes(toolsForCategory[i].appKey);
    //The app should not be in both staff pick and recommended row in order to display In the category section

    if (isLoggedIn && !isInStaffPick && !isRecommended) {
      featuredTools.push(toolsForCategory[i]);
    }

    if (!isLoggedIn && !isInStaffPick && !isIntent) {
      featuredTools.push(toolsForCategory[i]);
    }
  }

  /*
    When `toolsForCategory` array has 3 apps and 2 are in the `recommendedApps` array, we
    will only have one app here in the `featuredTools` array. From the requirements, we need
    to have at least 2 featured apps per pillar / category. At this point, we know that:

      - The `recommendedApps` array will contain the first 2 from `toolsForCategory` array
      - The `featuredTools` array will never contain any of the `recommendedApps` array

    Therefore, we can just push the first app from `toolsForCategory` array to `featuredTools.
  */
  // the first app in toolsForCategory should not be pushed to featuredTools if featuredTool already had that app
  if (featuredTools.length < 2 && toolsForCategory[0] !== featuredTools[0]) {
    featuredTools.push(toolsForCategory[0]);
  }

  return featuredTools;
};
